import type * as React from 'react'
import { Building2, UserCog } from 'lucide-react'
import { ORGANIZATION_ADMIN_NAME } from '@lib/constants/permissions'
import type { AssignableRolesDto } from '@lib/dto/user.dto'
import { FilterChip, FilterClear, FilterOption } from '@/components/ui/filter-chip'
import { staffRoleKey, type PersonRoleKey } from '@/components/people/personRole'

export interface UsersFilterState {
  /** Organization admins, clients, or staff on one of the company's roles. */
  role: PersonRoleKey | null
  venueId: string | null
}

export const EMPTY_USERS_FILTERS: UsersFilterState = {
  role: null,
  venueId: null,
}

export function usersFiltersActive(f: UsersFilterState): boolean {
  return f.role !== null || f.venueId !== null
}

/**
 * The roles to filter by: organization admins, each of the company's roles,
 * and clients where the list has them, which is for organization admins only.
 */
function roleOptions(roles: AssignableRolesDto): { value: PersonRoleKey; label: string }[] {
  return [
    { value: 'organizationAdmin', label: ORGANIZATION_ADMIN_NAME },
    ...roles.roles.map((role) => ({ value: staffRoleKey(role.id), label: role.name })),
    ...(roles.client ? [{ value: 'client' as const, label: 'Client' }] : []),
  ]
}

/**
 * The users table's filters — role and managed venue — for its `actions` bar,
 * beside "Invite user". A fragment, so the chips wrap in the bar's own row.
 */
export function UsersFilters({
  filters,
  onChange,
  venues,
  roles,
}: {
  filters: UsersFilterState
  onChange: (next: UsersFilterState) => void
  venues: { id: string; name: string }[]
  /** The company's roles, to filter by. */
  roles: AssignableRolesDto
}): React.ReactElement {
  const options = roleOptions(roles)
  const roleLabel = options.find((r) => r.value === filters.role)?.label ?? null
  const venueName = venues.find((v) => v.id === filters.venueId)?.name ?? null
  const active = usersFiltersActive(filters)

  return (
    <>

      <FilterChip
        property="Role"
        valueLabel={roleLabel}
        icon={UserCog}
        onClear={() => onChange({ ...filters, role: null })}
      >
        {(close) => (
          <>
            <FilterOption
              selected={filters.role === null}
              onSelect={() => {
                onChange({ ...filters, role: null })
                close()
              }}
            >
              Any role
            </FilterOption>
            {options.map((r) => (
              <FilterOption
                key={r.value}
                selected={filters.role === r.value}
                onSelect={() => {
                  onChange({ ...filters, role: r.value })
                  close()
                }}
              >
                {r.label}
              </FilterOption>
            ))}
          </>
        )}
      </FilterChip>

      <FilterChip
        property="Venue"
        valueLabel={venueName}
        icon={Building2}
        onClear={() => onChange({ ...filters, venueId: null })}
      >
        {(close) => (
          <>
            <FilterOption
              selected={filters.venueId === null}
              onSelect={() => {
                onChange({ ...filters, venueId: null })
                close()
              }}
            >
              Any venue
            </FilterOption>
            {venues.map((v) => (
              <FilterOption
                key={v.id}
                selected={filters.venueId === v.id}
                onSelect={() => {
                  onChange({ ...filters, venueId: v.id })
                  close()
                }}
              >
                {v.name}
              </FilterOption>
            ))}
            {venues.length === 0 ? (
              <p className="px-2 py-2 text-sm text-ink-muted">No venues yet.</p>
            ) : null}
          </>
        )}
      </FilterChip>

      {active ? <FilterClear onClear={() => onChange(EMPTY_USERS_FILTERS)} /> : null}
    </>
  )
}
